import { Fragment, useEffect } from 'react'

import { useIntersectionObserver } from '@nfinite/react-hubstairs/hooks'
import ErrorBoundary from 'components/Error/ErrorBoundary'
import Empty from 'components/Commons/Empty'

// eslint-disable-next-line no-restricted-imports
import { usePaginatedListQuery } from '../Provider'
import MasonryList, { TEMPLATE } from './Masonry'

import { ItemContainer } from './styled'

/**
 * A Paginated list wrapper for masonry grids
 */
function PaginatedListMasonry({
  columnGutter,
  columnWidth,
  itemsPerRow,
  placeholder,
  renderItem,
  insertElementAtIndex,
  empty,
  template,
  itemModals,
  filterItems,
  observeItemResize,
  rootRef,
}) {
  return (
    <PaginatedList
      filterItems={filterItems}
      rootRef={rootRef}
      renderItems={function RenderMasonryItems({ items, paging, placeholders }) {
        return (
          <MasonryList
            items={items}
            paging={paging}
            placeholders={placeholders}
            render={renderItem}
            columnGutter={columnGutter}
            columnWidth={columnWidth}
            itemsPerRow={itemsPerRow}
            placeholder={placeholder}
            template={template}
            observeItemResize={observeItemResize}
          />
        )
      }}
      itemModals={itemModals}
      renderEmptyList={function RenderEmptyMasonryList({ elements }) {
        return (
          <MasonryList
            items={elements}
            placeholders={[]}
            render={renderItem}
            placeholder={placeholder}
            template={template}
            observeItemResize={observeItemResize}
          />
        )
      }}
      insertElementAtIndex={insertElementAtIndex}
      empty={empty}
    />
  )
}

function EmptyList({ Container, empty, insertElementAtIndex, renderEmptyList }) {
  const elements = insertElementAtIndex?.length && getInsertedElements({ insertElementAtIndex })
  return (
    <>
      <>
        {elements?.length ? (
          renderEmptyList ? (
            renderEmptyList({ elements })
          ) : (
            <Container>{insertElementAtIndex.map(({ element }) => element)}</Container>
          )
        ) : null}
        {empty || <Empty dataTestId="paginated_list_default_empty" />}
      </>
    </>
  )
}

function getInsertedElements({ insertElementAtIndex }) {
  const items = []
  for (const { index, element } of insertElementAtIndex) {
    const toInsert = element
      ? {
          id: `hb-paginated-insert-${index}`,
          customElement: element,
        }
      : null
    if (toInsert && (items.length === 0 || items.length >= index)) {
      items.push(toInsert)
    }
  }
  return items
}

function getNumberPlaceholders(loading, numberItems = 0, nextLimit, paging, networkStatus) {
  if (paging?.hasNext === false) return 0
  if (!loading) return 0
  // Prevent displaying placeholders when the 1st page is being reloaded after navigating back to a cached paginated list
  if (numberItems > 0 && networkStatus === 1) return 0

  return paging?.totalCount ? Math.min(nextLimit, paging?.totalCount - numberItems) : nextLimit
}

function List({
  items = [],
  paging,
  Container,
  placeholder,
  numberPlaceholders,
  renderItem,
  itemKey,
  renderItems,
  insertElementAtIndex,
  dataTestId,
}) {
  const listItems = items.filter(i => i)

  if (insertElementAtIndex) {
    for (const { index, element } of insertElementAtIndex) {
      const toInsert = element
        ? {
            id: `hb-paginated-insert-${index}`,
            customElement: element,
          }
        : null
      if (toInsert && (items.length === 0 || listItems.length > index)) {
        listItems.splice(index, 0, toInsert)
      }
    }
  }

  const listPlaceholders =
    numberPlaceholders > 0
      ? [...new Array(numberPlaceholders)].map((i, index) => ({
          id: `hb-paginated-placeholders-${index}`,
          placeholder: true,
        }))
      : []

  if (renderItems) {
    return renderItems({ items: listItems, paging, placeholders: listPlaceholders, insertElementAtIndex })
  }

  const list = listItems.concat(listPlaceholders)

  return (
    <ItemContainer data-testid={dataTestId} as={Container}>
      {list.map((item, index) => {
        const uui = itemKey && itemKey(item, index)

        return (
          <Fragment key={uui || item.id}>
            {item.placeholder ? placeholder : item.customElement ? item.customElement : renderItem(item, index)}
          </Fragment>
        )
      })}
    </ItemContainer>
  )
}

/**
 * Infinite scroll pagination
 *
 * @param Container: React component declaration of the component wrapping all items
 * @param placeholder: React component to display in place of the items being fetched
 * @param rootRef: PaginatedList container - usefull for intersection (window by default)
 * @param children: Render props to render each item (item) => (<MyItem data={item} />)
 */
function PaginatedList({
  Container,
  placeholder,
  renderItem,
  itemKey,
  renderItems,
  insertElementAtIndex,
  rootRef,
  empty,
  onEmpty,
  dataTestId,
  renderEmptyList,
  headerElement,
  filterItems, // TODO an anomaly to be removed - only used in components/Project/Dashboard
  itemModals,
}) {
  const { items, paging, loading, nextLimit, error, fetchMore, refetch, networkStatus, skip } = usePaginatedListQuery()

  async function onIntersection(entry) {
    const { isIntersecting } = entry
    if (isIntersecting && paging?.hasNext && !loading) {
      const numberFetched = items?.length ?? 0
      fetchMore({
        variables: { paging: { skip: numberFetched, limit: nextLimit } },
      })
    }
  }

  const intersectRef = useIntersectionObserver(
    { root: rootRef?.current, rootMargin: '0px 0px 500px 0px' },
    onIntersection,
  )

  const filteredItems = filterItems ? filterItems(items) : items
  const isEmpty = filteredItems && filteredItems.length === 0

  const numberPlaceholders = getNumberPlaceholders(loading, filteredItems?.length, nextLimit, paging, networkStatus)

  useEffect(() => {
    isEmpty !== undefined && onEmpty && onEmpty(isEmpty)
  }, [isEmpty, onEmpty])

  return (
    <ErrorBoundary data={filteredItems} error={error} retry={refetch}>
      {isEmpty || skip ? (
        <EmptyList
          empty={empty}
          Container={Container}
          insertElementAtIndex={insertElementAtIndex}
          renderEmptyList={renderEmptyList}
        />
      ) : (
        <>
          {headerElement}
          <List
            Container={Container}
            items={filteredItems}
            paging={paging}
            placeholder={placeholder}
            numberPlaceholders={numberPlaceholders}
            refetch={refetch}
            renderItem={renderItem}
            itemKey={itemKey}
            renderItems={renderItems}
            insertElementAtIndex={insertElementAtIndex}
            dataTestId={dataTestId}
          />
          <div ref={intersectRef} style={{ height: 0, width: '100%' }} />
          {itemModals}
        </>
      )}
    </ErrorBoundary>
  )
}

PaginatedList.Masonry = PaginatedListMasonry

export { PaginatedList, TEMPLATE as MASONRY_TEMPLATE }
